import { BaseClient } from '../BaseClient';
import { Empty } from '../compiled-protos/common';
import {
  ScheduleAssignment,
  ScheduleAssignmentBoard,
  ScheduleAssignmentBoardList,
  ScheduleAssignmentList,
  ScheduleAssignmentRole,
  ScheduleAssignmentRoleList,
  ScheduleConflictResolution,
  ScheduleConflictResolutionList,
  ScheduleConflictType,
  ScheduleConflictTypeList,
  ScheduleEmail,
  ScheduleEmailRequest,
  ScheduleEmailSendResult,
  ScheduleEntryType,
  ScheduleEntryTypeList,
  ScheduleJobHours,
  ScheduleJobHoursList,
  ScheduleJobShift,
  ScheduleJobShiftList,
  ScheduleLodgingExclusion,
  ScheduleLodgingExclusionList,
  ScheduleLodgingLink,
  ScheduleLodgingLinkList,
  ScheduleLookahead,
  ScheduleLookaheadRequest,
  SchedulePerDiemLink,
  SchedulePerDiemLinkList,
  SchedulePublish,
  SchedulePublishList,
  ScheduleShift,
  ScheduleShiftList,
  ScheduleShiftType,
  ScheduleShiftTypeList,
  ScheduleTeam,
  ScheduleTeamList,
  ScheduleTeamMember,
  ScheduleTeamMemberList,
  ScheduleTimeoffWindow,
  ScheduleTimeoffWindowList,
  ScheduleTradeRequest,
  ScheduleTradeRequestList,
  ScheduleTravelDay,
  ScheduleTravelDayList,
  ScheduleTravelLine,
  ScheduleTravelRequest,
  ScheduleTravelResult,
  ScheduleTravelTier,
  ScheduleTravelTierList,
  ScheduleTravelTierRequest,
  ScheduleWeekException,
  ScheduleWeekExceptionList,
} from '../compiled-protos/schedule';
import { ScheduleServiceClient } from '../compiled-protos/schedule.client';

class ScheduleClient extends BaseClient {
  self: ScheduleServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new ScheduleServiceClient(this.transport);
  }

  // Shifts (one department job on one shift lane: the board card)

  public async CreateShift(req: ScheduleShift): Promise<ScheduleShift | undefined> {
    let res = ScheduleShift.create();
    try {
      res = await this.self.createScheduleShift(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetShift(req: ScheduleShift): Promise<ScheduleShift | undefined> {
    let res = ScheduleShift.create();
    try {
      res = await this.self.getScheduleShift(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetShift(req: ScheduleShift): Promise<ScheduleShiftList | undefined> {
    let res = ScheduleShiftList.create();
    try {
      res = await this.self.batchGetScheduleShift(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateShift(req: ScheduleShift): Promise<ScheduleShift | undefined> {
    let res = ScheduleShift.create();
    try {
      res = await this.self.updateScheduleShift(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteShift(req: ScheduleShift): Promise<Empty | undefined> {
    let res = Empty.create();
    try {
      res = await this.self.deleteScheduleShift(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Assignments (one person, one shift, one date). Reads come from the rpc view.

  public async CreateAssignment(req: ScheduleAssignment): Promise<ScheduleAssignment> {
    return await this.self.createScheduleAssignment(req, this.getMetaData()).response;
  }

  public async GetAssignment(req: ScheduleAssignment): Promise<ScheduleAssignment | undefined> {
    let res = ScheduleAssignment.create();
    try {
      res = await this.self.getScheduleAssignment(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetAssignment(
    req: ScheduleAssignment,
  ): Promise<ScheduleAssignmentList | undefined> {
    let res = ScheduleAssignmentList.create();
    try {
      res = await this.self.batchGetScheduleAssignment(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateAssignment(req: ScheduleAssignment): Promise<ScheduleAssignment> {
    return await this.self.updateScheduleAssignment(req, this.getMetaData()).response;
  }

  public async DeleteAssignment(req: ScheduleAssignment): Promise<Empty> {
    return await this.self.deleteScheduleAssignment(req, this.getMetaData()).response;
  }

  /** Stamps when the assignee first opened the day; a row already seen keeps its stamp. */
  public async MarkAssignmentSeen(
    req: ScheduleAssignment,
  ): Promise<ScheduleAssignment | undefined> {
    try {
      return await this.self.markScheduleAssignmentSeen(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  // Board: the effective row per slot (draft over published) the board renders from

  public async BatchGetBoard(
    req: ScheduleAssignmentBoard,
  ): Promise<ScheduleAssignmentBoardList | undefined> {
    let res = ScheduleAssignmentBoardList.create();
    try {
      res = await this.self.batchGetScheduleAssignmentBoard(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Publish

  public async GetPublish(req: SchedulePublish): Promise<SchedulePublish | undefined> {
    try {
      return await this.self.getSchedulePublish(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  public async BatchGetPublish(req: SchedulePublish): Promise<SchedulePublishList | undefined> {
    let res = SchedulePublishList.create();
    try {
      res = await this.self.batchGetSchedulePublish(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /** Stamps the listed drafts and deactivates the rows they supersede, in one transaction. */
  public async PublishAssignments(req: SchedulePublish): Promise<SchedulePublish> {
    return await this.self.publishScheduleAssignments(req, this.getMetaData()).response;
  }

  // Previews: rendered, never sent

  public async GetEmailPreview(req: ScheduleEmailRequest): Promise<ScheduleEmail> {
    return await this.self.getScheduleEmailPreview(req, this.getMetaDataWithoutCache()).response;
  }

  public async GetDepartmentEmailPreview(req: ScheduleEmailRequest): Promise<ScheduleEmail> {
    return await this.self.getScheduleDepartmentEmailPreview(req, this.getMetaDataWithoutCache()).response;
  }

  public async GetLookahead(req: ScheduleLookaheadRequest): Promise<ScheduleLookahead> {
    return await this.self.getScheduleLookahead(req, this.getMetaDataWithoutCache()).response;
  }

  /** Each job's scheduled hours across every week beside its Unity Build labor budget. */
  public async BatchGetJobHours(req: ScheduleJobHours): Promise<ScheduleJobHoursList | undefined> {
    let res = ScheduleJobHoursList.create();
    try {
      res = await this.self.batchGetScheduleJobHours(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async SendPublishEmails(req: SchedulePublish): Promise<ScheduleEmailSendResult> {
    return await this.self.sendSchedulePublishEmails(req, this.getMetaData()).response;
  }

  // Week exceptions ("not needed this week")

  public async CreateWeekException(
    req: ScheduleWeekException,
  ): Promise<ScheduleWeekException | undefined> {
    let res = ScheduleWeekException.create();
    try {
      res = await this.self.createScheduleWeekException(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetWeekException(
    req: ScheduleWeekException,
  ): Promise<ScheduleWeekExceptionList | undefined> {
    let res = ScheduleWeekExceptionList.create();
    try {
      res = await this.self.batchGetScheduleWeekException(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteWeekException(req: ScheduleWeekException): Promise<Empty | undefined> {
    let res = Empty.create();
    try {
      res = await this.self.deleteScheduleWeekException(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Job shift profiles: what a new week's card for a job and lane starts with

  public async CreateJobShift(req: ScheduleJobShift): Promise<ScheduleJobShift | undefined> {
    let res = ScheduleJobShift.create();
    try {
      res = await this.self.createScheduleJobShift(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetJobShift(req: ScheduleJobShift): Promise<ScheduleJobShift | undefined> {
    let res = ScheduleJobShift.create();
    try {
      res = await this.self.getScheduleJobShift(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetJobShift(req: ScheduleJobShift): Promise<ScheduleJobShiftList | undefined> {
    let res = ScheduleJobShiftList.create();
    try {
      res = await this.self.batchGetScheduleJobShift(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateJobShift(req: ScheduleJobShift): Promise<ScheduleJobShift> {
    return await this.self.updateScheduleJobShift(req, this.getMetaData()).response;
  }

  public async DeleteJobShift(req: ScheduleJobShift): Promise<Empty | undefined> {
    let res = Empty.create();
    try {
      res = await this.self.deleteScheduleJobShift(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Lodging links

  public async CreateLodgingLink(
    req: ScheduleLodgingLink,
  ): Promise<ScheduleLodgingLink | undefined> {
    let res = ScheduleLodgingLink.create();
    try {
      res = await this.self.createScheduleLodgingLink(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetLodgingLink(
    req: ScheduleLodgingLink,
  ): Promise<ScheduleLodgingLinkList | undefined> {
    let res = ScheduleLodgingLinkList.create();
    try {
      res = await this.self.batchGetScheduleLodgingLink(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Lodging exclusions: people taken off a scheduler-raised request

  public async CreateLodgingExclusion(
    req: ScheduleLodgingExclusion,
  ): Promise<ScheduleLodgingExclusion | undefined> {
    let res = ScheduleLodgingExclusion.create();
    try {
      res = await this.self.createScheduleLodgingExclusion(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetLodgingExclusion(
    req: ScheduleLodgingExclusion,
  ): Promise<ScheduleLodgingExclusionList | undefined> {
    let res = ScheduleLodgingExclusionList.create();
    try {
      res = await this.self.batchGetScheduleLodgingExclusion(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateLodgingExclusion(
    req: ScheduleLodgingExclusion,
  ): Promise<ScheduleLodgingExclusion | undefined> {
    let res = ScheduleLodgingExclusion.create();
    try {
      res = await this.self.updateScheduleLodgingExclusion(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Conflicts: the kinds and the resolutions a scheduler records against them

  public async BatchGetConflictType(
    req: ScheduleConflictType,
  ): Promise<ScheduleConflictTypeList | undefined> {
    let res = ScheduleConflictTypeList.create();
    try {
      res = await this.self.batchGetScheduleConflictType(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CreateConflictResolution(
    req: ScheduleConflictResolution,
  ): Promise<ScheduleConflictResolution | undefined> {
    let res = ScheduleConflictResolution.create();
    try {
      res = await this.self.createScheduleConflictResolution(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetConflictResolution(
    req: ScheduleConflictResolution,
  ): Promise<ScheduleConflictResolution | undefined> {
    let res = ScheduleConflictResolution.create();
    try {
      res = await this.self.getScheduleConflictResolution(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetConflictResolution(
    req: ScheduleConflictResolution,
  ): Promise<ScheduleConflictResolutionList | undefined> {
    let res = ScheduleConflictResolutionList.create();
    try {
      res = await this.self.batchGetScheduleConflictResolution(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateConflictResolution(
    req: ScheduleConflictResolution,
  ): Promise<ScheduleConflictResolution | undefined> {
    let res = ScheduleConflictResolution.create();
    try {
      res = await this.self.updateScheduleConflictResolution(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Trade requests: card slots for a day that name a department instead of a person

  public async CreateTradeRequest(
    req: ScheduleTradeRequest,
  ): Promise<ScheduleTradeRequest | undefined> {
    let res = ScheduleTradeRequest.create();
    try {
      res = await this.self.createScheduleTradeRequest(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetTradeRequest(
    req: ScheduleTradeRequest,
  ): Promise<ScheduleTradeRequestList | undefined> {
    let res = ScheduleTradeRequestList.create();
    try {
      res = await this.self.batchGetScheduleTradeRequest(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateTradeRequest(
    req: ScheduleTradeRequest,
  ): Promise<ScheduleTradeRequest | undefined> {
    let res = ScheduleTradeRequest.create();
    try {
      res = await this.self.updateScheduleTradeRequest(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteTradeRequest(req: ScheduleTradeRequest): Promise<Empty | undefined> {
    try {
      return await this.self.deleteScheduleTradeRequest(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  // Teams: named sets of people from any department, used to filter the people rail

  public async CreateTeam(req: ScheduleTeam): Promise<ScheduleTeam | undefined> {
    let res = ScheduleTeam.create();
    try {
      res = await this.self.createScheduleTeam(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetTeam(req: ScheduleTeam): Promise<ScheduleTeamList | undefined> {
    let res = ScheduleTeamList.create();
    try {
      res = await this.self.batchGetScheduleTeam(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateTeam(req: ScheduleTeam): Promise<ScheduleTeam | undefined> {
    let res = ScheduleTeam.create();
    try {
      res = await this.self.updateScheduleTeam(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteTeam(req: ScheduleTeam): Promise<Empty | undefined> {
    try {
      return await this.self.deleteScheduleTeam(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  public async CreateTeamMember(req: ScheduleTeamMember): Promise<ScheduleTeamMember | undefined> {
    let res = ScheduleTeamMember.create();
    try {
      res = await this.self.createScheduleTeamMember(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetTeamMember(
    req: ScheduleTeamMember,
  ): Promise<ScheduleTeamMemberList | undefined> {
    let res = ScheduleTeamMemberList.create();
    try {
      res = await this.self.batchGetScheduleTeamMember(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteTeamMember(req: ScheduleTeamMember): Promise<Empty | undefined> {
    try {
      return await this.self.deleteScheduleTeamMember(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  // Per diem links and the travel build

  public async BatchGetPerDiemLink(
    req: SchedulePerDiemLink,
  ): Promise<SchedulePerDiemLinkList | undefined> {
    let res = SchedulePerDiemLinkList.create();
    try {
      res = await this.self.batchGetSchedulePerDiemLink(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BuildTravel(req: ScheduleTravelRequest): Promise<ScheduleTravelResult | undefined> {
    let res = ScheduleTravelResult.create();
    try {
      res = await this.self.buildScheduleTravel(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Lookups

  public async BatchGetShiftType(req: ScheduleShiftType): Promise<ScheduleShiftTypeList | undefined> {
    let res = ScheduleShiftTypeList.create();
    try {
      res = await this.self.batchGetScheduleShiftType(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetEntryType(req: ScheduleEntryType): Promise<ScheduleEntryTypeList | undefined> {
    let res = ScheduleEntryTypeList.create();
    try {
      res = await this.self.batchGetScheduleEntryType(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetAssignmentRole(
    req: ScheduleAssignmentRole,
  ): Promise<ScheduleAssignmentRoleList | undefined> {
    let res = ScheduleAssignmentRoleList.create();
    try {
      res = await this.self.batchGetScheduleAssignmentRole(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Time off normalised to calendar days

  public async BatchGetTimeoffWindow(
    req: ScheduleTimeoffWindow,
  ): Promise<ScheduleTimeoffWindowList | undefined> {
    let res = ScheduleTimeoffWindowList.create();
    try {
      res = await this.self.batchGetScheduleTimeoffWindow(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Lodging and per diem coverage per person per scheduled day

  public async BatchGetTravelDay(
    req: ScheduleTravelDay,
  ): Promise<ScheduleTravelDayList | undefined> {
    let res = ScheduleTravelDayList.create();
    try {
      res = await this.self.batchGetScheduleTravelDay(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // One-way drive time home to job site per person per job in a week, drafts included

  public async BatchGetTravelTier(
    req: ScheduleTravelTierRequest,
  ): Promise<ScheduleTravelTierList | undefined> {
    let res = ScheduleTravelTierList.create();
    try {
      res = await this.self.batchGetScheduleTravelTier(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }
}

export {
  ScheduleAssignment,
  ScheduleAssignmentBoard,
  ScheduleAssignmentBoardList,
  ScheduleAssignmentList,
  ScheduleAssignmentRole,
  ScheduleAssignmentRoleList,
  ScheduleClient,
  ScheduleConflictResolution,
  ScheduleConflictResolutionList,
  ScheduleConflictType,
  ScheduleConflictTypeList,
  ScheduleEmail,
  ScheduleEmailRequest,
  ScheduleEmailSendResult,
  ScheduleEntryType,
  ScheduleEntryTypeList,
  ScheduleJobHours,
  ScheduleJobHoursList,
  ScheduleJobShift,
  ScheduleJobShiftList,
  ScheduleLodgingExclusion,
  ScheduleLodgingExclusionList,
  ScheduleLodgingLink,
  ScheduleLodgingLinkList,
  SchedulePerDiemLink,
  SchedulePerDiemLinkList,
  ScheduleTravelDay,
  ScheduleTravelDayList,
  ScheduleTravelLine,
  ScheduleTravelRequest,
  ScheduleTravelResult,
  ScheduleTravelTier,
  ScheduleTravelTierList,
  ScheduleTravelTierRequest,
  ScheduleLookahead,
  ScheduleLookaheadRequest,
  SchedulePublish,
  SchedulePublishList,
  ScheduleShift,
  ScheduleShiftList,
  ScheduleShiftType,
  ScheduleShiftTypeList,
  ScheduleTeam,
  ScheduleTeamList,
  ScheduleTeamMember,
  ScheduleTeamMemberList,
  ScheduleTimeoffWindow,
  ScheduleTimeoffWindowList,
  ScheduleTradeRequest,
  ScheduleTradeRequestList,
  ScheduleWeekException,
  ScheduleWeekExceptionList,
};
